import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  persistProductsListSearch,
  productsListNavState,
} from '@/lib/productsListNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TableCell,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MarginBadge, ProductIdentity, ProductStatusBadge } from '@/components/products';
import { MetricGrid, SummaryMetricCard, TablePagination } from '@/components/global';
import {
  InventoryDataTableCard,
  InventoryListFiltersCard,
  InventoryTableHead,
} from '@/components/inventory';
import { Plus, Search, MoreHorizontal, Eye, Pencil, Trash2, Package, PackageCheck, AlertTriangle, Percent } from 'lucide-react';
import { RoleGuard } from '@/components/auth/RoleGuard';

interface Category {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: 'HUB' | 'SPOKE';
}

interface Venue {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  barcode: string | null;
  categoryId: string | null;
  supplierId: string;
  purchasePrice: number;
  sellPrice: number;
  targetMarginPercent: number;
  isActive: boolean;
  status?: 'ACTIVE' | 'DORMANT' | 'DELISTED' | 'UNMANAGED';
  brand?: { id: string; name: string } | null;
  category?: Category | null;
  supplier?: Supplier | null;
  marginPercent?: number;
  ragStatus?: 'green' | 'amber' | 'red';
  /** Server-resolved thumbnail for list grids (prefer imageUrl, else first catalog image). */
  thumbnailUrl?: string | null;
  imageUrl?: string | null;
  media?: Array<{ url: string; mediaType: string }> | null;
}

interface PaginatedResult {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
}

const PRODUCT_STATUS_FILTERS = ['all', 'ACTIVE', 'DORMANT', 'DELISTED', 'UNMANAGED'] as const;
type ProductStatusFilter = (typeof PRODUCT_STATUS_FILTERS)[number];

function parseStatusFilter(searchParams: URLSearchParams): ProductStatusFilter {
  const status = searchParams.get('status');
  if (
    status === 'ACTIVE' ||
    status === 'DORMANT' ||
    status === 'DELISTED' ||
    status === 'UNMANAGED' ||
    status === 'all'
  ) {
    return status;
  }
  const legacy = searchParams.get('isActive');
  if (legacy === 'true') return 'ACTIVE';
  if (legacy === 'false') return 'DELISTED';
  return 'all';
}

function appendStatusFilter(params: URLSearchParams, statusFilter: ProductStatusFilter): void {
  if (statusFilter !== 'all') {
    params.set('status', statusFilter);
  }
}

function formatCurrency(value: number): string {
  return `£${Number(value).toFixed(2)}`;
}

function appendLocationParams(params: URLSearchParams, locationFilter: string): void {
  if (locationFilter.startsWith('w:')) {
    params.set('warehouseId', locationFilter.slice(2));
  } else if (locationFilter.startsWith('v:')) {
    params.set('venueId', locationFilter.slice(2));
  }
}

export default function ProductsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [pageSize] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoryFilter, setCategoryFilter] = useState(searchParams.get('categoryId') || 'all');
  const [statusFilter, setStatusFilter] = useState<ProductStatusFilter>(() =>
    parseStatusFilter(searchParams)
  );
  const [marginFilter, setMarginFilter] = useState(searchParams.get('belowTargetMargin') || 'all');
  const [locationFilter, setLocationFilter] = useState(searchParams.get('locationId') || 'all');
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    belowMargin: 0,
    avgMargin: null as number | null,
  });

  const loadSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (categoryFilter && categoryFilter !== 'all') params.set('categoryId', categoryFilter);
      appendStatusFilter(params, statusFilter);
      appendLocationParams(params, locationFilter);

      const res = await apiFetch(`/api/v1/products/summary?${params}`);
      if (!res.ok) throw new Error('Failed to load product summary');
      const data: {
        total: number;
        activeCount: number;
        belowTargetMarginCount: number;
        avgMarginPercent: number | null;
      } = await res.json();

      setStats({
        total: data.total,
        active: data.activeCount,
        belowMargin: data.belowTargetMarginCount,
        avgMargin: data.avgMarginPercent,
      });
    } catch (error) {
      console.error('Error loading product summary:', error);
    }
  }, [search, categoryFilter, statusFilter, locationFilter]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (categoryFilter && categoryFilter !== 'all') params.set('categoryId', categoryFilter);
      appendStatusFilter(params, statusFilter);
      if (marginFilter === 'below') params.set('belowTargetMargin', 'true');
      appendLocationParams(params, locationFilter);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));

      const res = await apiFetch(`/api/v1/products?${params}`);
      if (!res.ok) throw new Error('Failed to load products');
      const data: PaginatedResult = await res.json();
      setProducts(data.items);
      setTotal(data.total);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, statusFilter, marginFilter, locationFilter, page, pageSize]);

  const loadFilters = useCallback(async () => {
    try {
      const [categoryRes, warehouseRes, venueRes] = await Promise.all([
        apiFetch('/api/v1/categories'),
        apiFetch('/api/v1/warehouses'),
        apiFetch('/api/v1/venues?pageSize=500'),
      ]);

      if (categoryRes.ok) {
        const data = await categoryRes.json();
        setCategories(Array.isArray(data) ? data : data.items ?? []);
      }

      if (warehouseRes.ok) {
        const data = await warehouseRes.json();
        setWarehouses(data.items ?? data ?? []);
      }

      if (venueRes.ok) {
        const data = await venueRes.json();
        setVenues(data.items ?? data ?? []);
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const buildListSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (categoryFilter && categoryFilter !== 'all') params.set('categoryId', categoryFilter);
    appendStatusFilter(params, statusFilter);
    if (marginFilter && marginFilter !== 'all') params.set('belowTargetMargin', marginFilter);
    if (locationFilter !== 'all') params.set('locationId', locationFilter);
    if (page > 1) params.set('page', String(page));
    return params.toString();
  }, [search, categoryFilter, statusFilter, marginFilter, locationFilter, page]);

  useEffect(() => {
    const query = buildListSearch();
    persistProductsListSearch(query);
    setSearchParams(new URLSearchParams(query));
  }, [buildListSearch, setSearchParams]);

  const getListNav = useCallback(
    () => productsListNavState(buildListSearch()),
    [buildListSearch]
  );

  async function handleDelete(product: Product) {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      const res = await apiFetch(`/api/v1/products/${product.id}`, { method: 'DELETE' });
      if (res.ok) {
        loadProducts();
        loadSummary();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-2xl font-semibold">Products</h1>
          <RoleGuard permission="products.write">
            <Button onClick={() => navigate('/inventory/products/new', getListNav())}>
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </RoleGuard>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Total products"
            value={stats.total}
            icon={Package}
          />
          <SummaryMetricCard
            label="Active products"
            value={stats.active}
            icon={PackageCheck}
          />
          <SummaryMetricCard
            label="Margin alerts"
            value={stats.belowMargin}
            icon={AlertTriangle}
            status={stats.belowMargin > 0 ? 'red' : undefined}
          />
          <SummaryMetricCard
            label="Avg. margin"
            value={
              stats.avgMargin === null
                ? 'N/A'
                : `${stats.avgMargin.toFixed(1)}%`
            }
            icon={Percent}
          />
        </MetricGrid>

        <InventoryListFiltersCard>
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, SKU, barcode..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>
          <Select
            value={categoryFilter}
            onValueChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as ProductStatusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All managed</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="DORMANT">Dormant</SelectItem>
              <SelectItem value="DELISTED">Delisted</SelectItem>
              <SelectItem value="UNMANAGED">Unmanaged</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={locationFilter}
            onValueChange={(v) => {
              setLocationFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All locations</SelectItem>
              {warehouses.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Warehouses</SelectLabel>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={`w:${w.id}`}>
                      {warehouseOptionLabel(w)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {venues.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Venues</SelectLabel>
                  {venues.map((v) => (
                    <SelectItem key={v.id} value={`v:${v.id}`}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <Select
            value={marginFilter}
            onValueChange={(v) => {
              setMarginFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="All Margins" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Margins</SelectItem>
              <SelectItem value="below">Margin alerts</SelectItem>
            </SelectContent>
          </Select>
        </InventoryListFiltersCard>

        <InventoryDataTableCard
          loading={loading}
          isEmpty={products.length === 0}
          colSpan={8}
          emptyMessage="No products found"
          header={
            <>
              <InventoryTableHead>Product</InventoryTableHead>
              <InventoryTableHead className="hidden md:table-cell">SKU</InventoryTableHead>
              <InventoryTableHead className="hidden lg:table-cell">Category</InventoryTableHead>
              <InventoryTableHead className="text-right lg:table-cell">Purchase</InventoryTableHead>
              <InventoryTableHead className="hidden text-right lg:table-cell">Sell</InventoryTableHead>
              <InventoryTableHead className="hidden md:table-cell">Margin</InventoryTableHead>
              <InventoryTableHead className="hidden sm:table-cell">Status</InventoryTableHead>
              <InventoryTableHead className="w-[50px]" />
            </>
          }
          footer={
            !loading && products.length > 0 ? (
              <TablePagination
                page={page}
                totalPages={totalPages}
                totalItems={total}
                itemLabel="products"
                pageSize={pageSize}
                onPageChange={setPage}
              />
            ) : undefined
          }
        >
          {products.map((product) => (
            <TableRow key={product.id}>
              <TableCell>
                <ProductIdentity
                  name={product.name}
                  sku={product.sku}
                  product={product}
                  productId={product.id}
                  showSku={false}
                />
              </TableCell>
              <TableCell className="hidden font-mono text-sm md:table-cell">{product.sku}</TableCell>
              <TableCell className="hidden lg:table-cell">{product.category?.name || '—'}</TableCell>
              <TableCell className="text-right">
                {product.purchasePrice > 0 ? (
                  formatCurrency(product.purchasePrice)
                ) : (
                  <span className="text-muted-foreground">N/A</span>
                )}
              </TableCell>
              <TableCell className="hidden text-right lg:table-cell">
                {formatCurrency(product.sellPrice)}
              </TableCell>
              <TableCell className="hidden md:table-cell">
                {product.marginPercent !== undefined ? (
                  <MarginBadge
                    marginPercent={product.marginPercent}
                    targetMargin={product.targetMarginPercent}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">N/A</span>
                )}
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <ProductStatusBadge status={product.status || (product.isActive ? 'ACTIVE' : 'DELISTED')} />
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => navigate(`/inventory/products/${product.id}`, getListNav())}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      View
                    </DropdownMenuItem>
                    <RoleGuard permission="products.write">
                      <DropdownMenuItem
                        onClick={() => navigate(`/inventory/products/${product.id}/edit`, getListNav())}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(product)}
                        className="text-red-600"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </RoleGuard>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </InventoryDataTableCard>
      </div>
    </div>
  );
}
